import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid2 as Grid,
  Paper,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Invoice01Icon,
  ShoppingBag01Icon,
  Tag01Icon,
  WhatsappIcon,
} from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import MenuSidebarNavigation from "@/components/MenuSidebarNavigation";
import OrderItemsSummary from "@/components/OrderDetails/components/OrderItemsSummary";
import PhoneLoginDrawer from "@/components/loyalty/PhoneLoginDrawer";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { isLoggedInSelector, userSelector } from "@/features/auth/slice";
import { openPhoneLoginDrawer } from "@/store/uiSlice";
import { Order } from "@/types";
import { Currency } from "@/types/currency";
import StatusView from "./StatusView";
import PostOrderSurveyCard from "./PostOrderSurveyCard";
import OrderHistoryDrawer from "./OrderHistoryDrawer";

export interface OrderStatusViewProps {
  order: Order;
  orderNumber?: number;
  isCompleted: boolean;
  organizationId?: string;
  organizationAlias?: string;
  orderId?: string;
  surveyId?: string | null;
  currency?: keyof typeof Currency;
  subtotalValue: number;
  deliveryValue: number | string | null;
  taxesValue: number | null;
  totalValue: number;
  onGoBack: () => void;
  onChat: () => void;
}

const OrderStatusDesktop: React.FC<OrderStatusViewProps> = ({
  order,
  orderNumber,
  isCompleted,
  organizationId,
  organizationAlias,
  orderId,
  surveyId,
  currency = "NIO",
  subtotalValue,
  deliveryValue,
  taxesValue,
  totalValue,
  onGoBack,
  onChat,
}) => {
  const { palette } = useTheme();
  const dispatch = useAppDispatch();
  const user = useAppSelector(userSelector);
  const isLoggedInRedux = useAppSelector(isLoggedInSelector);
  const isLoggedIn =
    isLoggedInRedux || Boolean(user && user.uid && (user.phone || user.email || user.name));

  const [isHistoryDrawerOpen, setIsHistoryDrawerOpen] = useState(false);
  const [pendingHistoryOpen, setPendingHistoryOpen] = useState(false);

  useEffect(() => {
    if (isLoggedIn && pendingHistoryOpen) {
      setPendingHistoryOpen(false);
      setIsHistoryDrawerOpen(true);
    }
  }, [isLoggedIn, pendingHistoryOpen]);

  const handleOpenHistory = () => {
    if (!isLoggedIn) {
      setPendingHistoryOpen(true);
      dispatch(openPhoneLoginDrawer());
      return;
    }
    setIsHistoryDrawerOpen(true);
  };

  const displayOrderNumber = orderNumber ?? order?.number;

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", backgroundColor: "#DDEAFD", width: "100%" }}>
      <MenuSidebarNavigation />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "calc(100% - 280px)",
          ml: "280px",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Top Nav Bar */}
        <Paper square elevation={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            px={3}
            py={2}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body1" fontWeight={500}>
                Estatus del pedido
              </Typography>
              {displayOrderNumber ? (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <HugeiconsIcon icon={Tag01Icon} color={palette.primary.main} size={20} />
                  <Typography variant="body1" fontWeight={500}>
                    {displayOrderNumber}
                  </Typography>
                </Stack>
              ) : null}
            </Stack>

            <Button
              variant="contained"
              color="success"
              size="large"
              onClick={onChat}
              startIcon={<HugeiconsIcon icon={WhatsappIcon} />}
            >
              ¿Necesitas ayuda?
            </Button>
          </Stack>
        </Paper>

        {/* Main Content Grid */}
        <Container maxWidth="lg" sx={{ py: 3, flex: 1 }}>
          <Grid container spacing={3} alignItems="flex-start">
            {/* Left Column: Status (7 cols) */}
            <Grid size={{ xs: 12, lg: 7 }}>
              <Stack spacing={2}>
                <StatusView order={order} isCompleted={isCompleted} />

                {isCompleted && (
                  <PostOrderSurveyCard
                    organizationId={organizationId}
                    surveyId={surveyId}
                    order={order}
                  />
                )}
              </Stack>
            </Grid>

            {/* Right Column: Order Details & Actions (5 cols) */}
            <Grid size={{ xs: 12, lg: 5 }}>
              <Stack spacing={2}>
                <OrderItemsSummary
                  order={order}
                  title="Detalles del pedido"
                  subtotal={subtotalValue}
                  deliveryValue={deliveryValue}
                  taxesValue={taxesValue}
                  total={totalValue}
                  isMobile={false}
                  sx={{ borderRadius: "14px" }}
                  PaperProps={{
                    elevation: 1,
                  }}
                />

                <Paper elevation={1} sx={{ p: 2, borderRadius: "14px" }}>
                  <Stack spacing={1.5}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      onClick={onGoBack}
                      startIcon={<HugeiconsIcon icon={ShoppingBag01Icon} size={20} />}
                    >
                      Nuevo pedido
                    </Button>

                    <Button
                      variant="outlined"
                      size="large"
                      fullWidth
                      onClick={handleOpenHistory}
                      startIcon={<HugeiconsIcon icon={Invoice01Icon} size={20} />}
                    >
                      Historial de pedidos
                    </Button>
                  </Stack>
                </Paper>
              </Stack>
            </Grid>
          </Grid>
        </Container>

        <PhoneLoginDrawer />

        <OrderHistoryDrawer
          open={isHistoryDrawerOpen}
          onClose={() => setIsHistoryDrawerOpen(false)}
          currentOrderId={orderId}
          organizationId={organizationId}
          organizationAlias={organizationAlias}
          currency={currency}
        />
      </Box>
    </Box>
  );
};

export default OrderStatusDesktop;
